import {useEffect, useState} from "react";
import {createPortal} from "react-dom";
import {useLocale, useMessages} from "i18n";
import {useResolvedStorefrontQuote} from "pricing/useQuotedPricing";
import {useTrialExpiryOffer} from "./useTrialExpiryOffer";
import {offerFirstMonthFigures} from "./firstMonthPricing";
import {FirstMonthPlansDialog} from "./FirstMonthPlansDialog";
import {useCampaignCountdown} from "./useCampaignCountdown";
import {useOfferEvents} from "./useOfferEvents";
import {FirstMonthOfferTicket} from "./FirstMonthOfferTicket";
import type {BesideOfferPlacement} from "./besideOfferHost";

/**
 * The 50%-off offer as a referee's trial runs out (kitecorp/transferify-ui#1449) — the third moment of
 * kitecorp/transferify#117.
 *
 * <b>It hangs BESIDE the card on the coupon's ticket since ui#2949</b> (owner 2026-09-27): out of the
 * drop area, to the card's left where the success card's offers hang, and on a phone behind a tab that
 * opens over the card. The ticket because it is a first-month offer like the success card's campaign:
 * the same figures (`offerFirstMonthFigures`), the same deadline and scarcity copy
 * (`banner.firstMonthOffer`) and the same dialog behind the action, so one offer look rather than two.
 * It is still MOUNTED here, in `OfferCallouts`, so the over-limit checkout still suppresses it; it only
 * portals into the place the card's frame hands down ({@link BesideOfferPlacement}).
 *
 * <b>Not the post-signup callout's component</b>, and for its eligibility rule: this moment's reader reads as PAID (the api answers a trial as an
 * active BASIC subscription), which the post-signup rule refuses outright. Merging them into one
 * component would mean a component holding two contradictory eligibility rules and a flag to pick.
 *
 * <b>They cannot both appear.</b> The post-signup moment requires a free plan and this one requires a
 * live trial window, and a trial reader's plan fields are never free — so the notices slot shows at
 * most one offer without either component knowing about the other.
 *
 * <b>The deadline is on the callout</b> (kitecorp/transferify-ui#1599), for the reason the post-signup
 * one carries it: the reader decides whether to click here, so a deadline they only meet inside the
 * dialog arrives after the decision it was meant to inform. The callout uses inclusive days while the
 * dialog keeps the finer countdown; both omit an absent or passed deadline.
 *
 * Markup + wiring only: the window, the eligibility and the percentage all come from
 * {@link useTrialExpiryOffer}, which delegates to React-free modules. What the reader sees is the
 * first month and the renewal price that {@link offerFirstMonthFigures} derives from that percentage
 * and the storefront they are quoted from; no percentage is stated (kitecorp/transferify-ui#2545).
 */
export function TrialExpiryOfferCallout({beside}: {beside: BesideOfferPlacement}) {
	const {banner, pricing} = useMessages();
	const {locale} = useLocale();
	// The clock is read per render rather than held in state, so the eligibility window cannot freeze on
	// a long-lived tab. The countdown below re-renders this component on each minute boundary, which is
	// also what re-asks whether the trial window is still open.
	const {offer, plan, occasion, endsOn, spotsLeft, dismiss} = useTrialExpiryOffer(new Date());
	const [picking, setPicking] = useState(false);
	// `endsOn` is already null whenever there is no offer (see `useTrialExpiryOffer`), so an absent offer
	// arms no timer either way.
	const countdown = useCampaignCountdown(endsOn);
	// The storefront this reader is priced from, read only while there is an offer to price, and SETTLED
	// before any figure prints: the first render answers with the baked catalogue, and a reader whose
	// geography moves them to another storefront would read one price for a frame and then another.
	const {storefront, settled} = useResolvedStorefrontQuote(offer !== null && plan !== null);
	// No honest figure means no offer: an offer this client cannot price is one it must not describe.
	const figures = offer === null || plan === null || !settled ? null
		: offerFirstMonthFigures(storefront.pricing, plan, offer.percentOff, locale, pricing.welcome.thenMonthly);
	// Out of the way while a slide-out card stands where it would hang (`slideOutCardIsOut`).
	const present = figures !== null && !beside.yielding;
	const {announce, element} = beside;
	useEffect(() => { announce(present ? {dismiss} : null); }, [announce, present, dismiss]);
	useEffect(() => () => announce(null), [announce]);
	/*
	 * The funnel seam (api#999). SHOWN is the ticket actually on screen: the frame mounts `element` only
	 * while the tag is open, so a phone reader who never opens the tab was never shown the offer. The
	 * once-per-instance latch still matters: the countdown re-renders this on every minute boundary, and
	 * a per-render report would measure minutes on screen instead of readers.
	 */
	const {reportClaimed} = useOfferEvents("TRIAL_EXPIRY", present && element !== null, occasion);

	if (!present || offer === null || plan === null) {
		return null;
	}
	const copy = banner.trialExpiryOffer;
	return (
		<>
			{element !== null && createPortal(
				<FirstMonthOfferTicket testId="trial-expiry-offer-callout" factsTestId="trial-expiry-offer" eyebrow={copy.eyebrow}
					// The sentence the callout printed is the heading's accessible name, its terms included.
					title={`${copy.message(figures.amount, figures.note)} ${copy.detail}`}
					figures={figures} plan={plan} countdown={countdown} spotsLeft={spotsLeft}
					actionLabel={copy.action} onAction={() => {
						reportClaimed();
						setPicking(true);
					}}/>,
				element,
			)}
			<FirstMonthPlansDialog
				open={picking}
				moment="TRIAL_EXPIRY"
				preselected={plan}
				// The percentage this offer is already showing, so a failed pricing read inside the
				// dialog can still price the tiers from the bundle's own catalogue (ui#1671).
				percentOff={offer.percentOff}
				occasion={occasion}
				endsOn={endsOn}
				onClose={() => setPicking(false)}
			/>
		</>
	);
}
